import { mapGetters } from 'vuex'
import settingshelpers from '~/lib/settingshelpers'

// Canonical fallback order when the requested/preferred format isn't
// available for a report type. Mirrors the backend's
// ReportTask::FORMAT_PRIORITY / ScheduledreportResource::normalizeReportFormat
// so the frontend and backend can't silently pick different fallback formats.
const reportFormatPriority = ['xlsx', 'pdf', 'html']

const untranslatedReportTypes = [
  { 'id': 'sales', 'name': 'Sales report' },
  { 'id': 'accountingsalessimple', 'name': 'Sales report for accounting', 'props': ['nofilters'] },
  { 'id': 'groupedsales', 'name': 'Sales report by product type' },
  { 'id': 'channelsales', 'name': 'Sales report by sales channel' },
  { 'id': 'payments', 'name': 'Payments report' },
  { 'id': 'distributors', 'name': 'Distributors report', 'suffix': 'Movies' },
  { 'id': 'visitors', 'name': 'Visitors report', 'suffix': 'Movies' },
  { 'id': 'detailedvisitors', 'name': 'Detailed visitors report', 'suffix': 'Movies' },
  { 'id': 'limitedvisitors', 'name': 'Limited visitors report', 'suffix': 'Movies' },
  { 'id': 'participants', 'name': 'Participants report', 'suffix': 'Events' },
  { 'id': 'monthlyreservations', 'name': 'Monthly reservations report' },
  { 'id': 'weeklyreservations', 'name': 'Weekly reservations report' },
  { 'id': 'dailyreservations', 'name': 'Daily reservations report' },
  { 'id': 'hourlyreservations', 'name': 'Hourly reservations report' },
  { 'id': 'voucher', 'name': 'Voucher report' },
  { 'id': 'discount', 'name': 'Discount report' },
  { 'id': 'advances', 'name': 'Advances report' },
  { 'id': 'accountingadvances', 'name': 'Advances report for accounting' },
  { 'id': 'valuecards', 'name': 'Value cards report', 'props': ['nofilters'] },
  { 'id': 'gaspump', 'name': 'Gas pump report', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'noresourcefilter', 'nosaleschannelfilter'] },
  { 'id': 'agenda', 'name': 'Agenda report' },
  { 'id': 'productoccupancy', 'name': 'Product occupancy report' },
  { 'id': 'resourceoccupancy', 'name': 'Resource occupancy report', props: ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'nosaleschannelfilter', 'nodeliverymethodfilter', 'noposfilter'] },
  { 'id': 'serialticket', 'name': 'Serial ticket report for accounting' },
  { 'id': 'valuecardsaccounting', 'name': 'Value cards report for accounting' },
  { 'id': 'invoicelist', 'name': 'Invoice list', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'noresourcefilter', 'nosaleschannelfilter', 'nodeliverymethodfilter', 'nohtml', 'nopdf'] },
  { 'id': 'invoiceitemlist', 'name': 'Invoice item list', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'noresourcefilter', 'nosaleschannelfilter', 'nodeliverymethodfilter', 'nohtml', 'nopdf'] },
  { 'id': 'paymentlist', 'name': 'Payment list', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'noresourcefilter', 'nosaleschannelfilter', 'nodeliverymethodfilter', 'nohtml', 'nopdf'] },
  { 'id': 'deliverymanifest', 'name': 'Delivery Manifest report', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'noresourcefilter', 'nosaleschannelfilter', 'noposfilter'] },
  { 'id': 'customerpurchases', 'name': 'Customer purchases report', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'noresourcefilter', 'nosaleschannelfilter', 'noposfilter', 'nodeliverymethodfilter', 'customerfilter'] },
  { 'id': 'customercohort', 'name': 'Customer cohort report', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noresourcefilter', 'nosaleschannelfilter', 'noposfilter', 'nodeliverymethodfilter', 'followupproductfilter'] },
  { 'id': 'customerltv', 'name': 'Customer lifetime value', 'props': ['nocategoryfilter', 'noproducttypefilter', 'noproductfilter', 'noresourcefilter', 'nosaleschannelfilter', 'noposfilter', 'nodeliverymethodfilter', 'customerfilter', 'nopdf'] },
]
export default {
  data() {
    return {
      additionalReportTypes: []
    }
  },
  computed: {
    ...mapGetters(['userData']),
    allReportTypes () {
      const combinedTypes = [...untranslatedReportTypes, ...this.additionalReportTypes]
      return combinedTypes.map(t => {
        return {
          ...t,
          name: this._(t.name) + (t.suffix ? ' (' + this._(t.suffix) + ')' : '')
        }
      })
    },
    accountingReportTypes () {
      return this.reportTypes.filter(t => {
        return t.id === 'serialticket' || t.id.match(/accounting/)
      })
    },
    listReportTypes () {
      return this.reportTypes.filter(t => {
        return t.id.match(/list/)
      })
    },
    customerReportTypes () {
      if (!this.isFeatureEnabled('customerreports')) {
        return []
      }
      return this.reportTypes.filter(t => {
        return t.id === 'customerpurchases' || t.id === 'customercohort' || t.id === 'customerltv'
      })
    },
    movieReportTypes () {
      return this.reportTypes.filter(t => {
        return t.id.match(/visitors|distributors/)
      })
    },
    reservationReportTypes () {
      return this.reportTypes.filter(t => {
        return t.id.match(/reservations|occupancy/) || t.id === 'agenda' || t.id === 'accommodationstatistics'
      })
    },
    otherReportTypes () {
      return this.reportTypes.filter(t => {
        return !this.accountingReportTypes.includes(t) && 
               !this.movieReportTypes.includes(t) &&
               !this.reservationReportTypes.includes(t) &&
               !this.listReportTypes.includes(t) &&
               t.id !== 'customerpurchases' &&
               t.id !== 'customercohort' &&
               t.id !== 'customerltv' &&
               t.id !== 'advances' &&
               t.id !== 'valuecards'
      })
    },
    reportTypes () {
      return this.allReportTypes.sort((a, b) => a.name.localeCompare(b.name))
    }
  },
  methods: {
    isFeatureEnabled: settingshelpers.methods.isFeatureEnabled,
    addReportType(reportType) {
      this.additionalReportTypes.push(reportType)
    },
    isReportFormatSupported(reportFormat, reportType) {
      if (reportType === 'accommodationstatistics') {
        return false
      }
      const report = this.allReportTypes.find(t => t.id === reportType)
      if (report && report.props) {
        switch (reportFormat) {
          case 'html':
            return !report.props.includes('nohtml')
          case 'pdf':
            return !report.props.includes('nopdf')
          case 'xlsx':
            return !report.props.includes('noxlsx')
        }
      }
      return true
    },
    getReportName(reportType) {
      const report = this.allReportTypes.find(t => t.id === reportType)
      return report ? report.name : reportType
    },
    // Single source of truth for "which format should we actually use for this
    // report type", shared by archivedReportUrl (view/download links) and
    // CreateReport.vue's ensureSupportedFormat (format picker). Returns null
    // when no format is supported for reportType (e.g. accommodationstatistics,
    // which doesn't use this format concept at all).
    resolveSupportedFormat(reportType, preferredFormat) {
      if (reportType && preferredFormat && this.isReportFormatSupported(preferredFormat, reportType)) {
        return preferredFormat
      }
      for (const candidate of reportFormatPriority) {
        if (this.isReportFormatSupported(candidate, reportType)) {
          return candidate
        }
      }
      return null
    },
    archivedReportUrl(id, format, reportType) {
      const ext = reportType ? this.resolveSupportedFormat(reportType, format) : format
      if (!ext) {
        return null
      }
      return this.baseDomain + this.shopId + '/archivedreports/' + id + '.' + ext + '?apikey=' + this.apiKey
    }
  },
  mounted () {
    if (this.isFeatureEnabled('statisticsfinland')) {
      this.additionalReportTypes.push({ 'id': 'accommodationstatistics', name: this._('Accommodation statistics for Statistics Finland') })
    }
  }
}
